import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { validate, isEmpty, validateExt } from 'shared/utils/Validation';
import ScrollToTop from 'shared/utils/scrollToTop';
import { Row, Col, Form, ComponentLoader, FormGroup, Input, ErrorSummary } from '@snsw/react-component-library';
import { store } from 'shared/redux/configureStore';
import { PlantDesignApplicantDetailsPage } from 'plantDesign/constants/plantDesignApplicantDetailsPage';
import { storePlantDesignApplicantDetails } from 'plantDesign/redux/plantDesignApplicantDetailsSlice';
import { PasswordlessContinueSaveExitButtons } from 'shared/components/formNavigationButtons/PasswordlessContinueSaveExitButtons';
import { PagePath } from 'shared/constants/pagePath';
import { createApplication, saveApplication } from 'shared/redux/applicationSlice';
import { getPlantDesignlLicenceTypeCode } from 'shared/utils/Helpers';
import { storeRedirect } from 'plantItemNew/redux/slices/formSlice';
import { PlantDesignConstants } from 'plantDesign/constants/plantDesignConstants';

export const PlantDesignApplicantDetails = () => {
  ScrollToTop();

  const storedApplicantDetails = useSelector((state) => state.plantDesignApplicantDetails);
  const application = useSelector((state) => state.application);
  const refData = useSelector((state) => state.plantDesignReferenceData);
  const user = useSelector((state) => state.passwordlessOidc.user);
  const plantDesignLicenceTypes = useSelector((state) => state.plantDesignLicenceTypes);

  const navigate = useNavigate();

  const [firstName, setFirstName] = useState(storedApplicantDetails?.firstName || '');
  const [middleName, setMiddleName] = useState(storedApplicantDetails?.middleName || '');
  const [lastName, setLastName] = useState(storedApplicantDetails?.lastName || '');
  const [employer, setEmployer] = useState(storedApplicantDetails?.employer || '');
  const [jobTitle, setJobTitle] = useState(storedApplicantDetails?.jobTitle || '');

  // TODO: Retrieve the email from the IDP
  const [emailAddress, setEmailAddress] = useState(storedApplicantDetails?.email);

  const [showErrors, setShowErrors] = useState(false);
  const [errorsSummary, setErrorsSummary] = useState([]);

  const onContinueClick = () => {
    const isValid = validateExt(validators, setErrors, setErrorsSummary);
    setShowErrors(!isValid);
    if (!isValid) {
      window.scrollTo(0, 0);
      return;
    }

    storeApplicantDetails(isValid);
    store
      .dispatch(saveApplication(true))
      .unwrap()
      .then(() => {
        navigate(PagePath.plantDesignRegistrationHolder);
      })
      .catch(() => {});
  };

  const onPreSaving = () => {
    let isCompleted = validate(validators, setErrors);
    storeApplicantDetails(isCompleted);
  };

  useEffect(() => {
    setEmailAddress(user.profile.email);
    store.dispatch(storeRedirect(''));
  }, [user]);

  useEffect(() => {
    const licenceCode = getPlantDesignlLicenceTypeCode(plantDesignLicenceTypes);
    licenceCode && store.dispatch(createApplication(licenceCode));
  }, [plantDesignLicenceTypes]);

  useEffect(() => {
    if (showErrors) validateExt(validators, setErrors, setErrorsSummary);
  }, [firstName, middleName, lastName]);

  const validators = [
    {
      fieldName: 'firstName',
      fieldValue: firstName,
      isValid: (v) => !isEmpty(v) || lastName,
      errorMessage: () => 'First name or last name is required',
    },
    {
      fieldName: 'lastName',
      fieldValue: lastName,
      isValid: (v) => !isEmpty(v) || firstName,
      errorMessage: () => 'First name or last name is required',
    },
  ];

  const [errors, setErrors] = useState({
    firstName: false,
    middleName: false,
    lastName: false,
  });

  const storeApplicantDetails = (isCompleted) => {
    store.dispatch(
      storePlantDesignApplicantDetails({
        firstName,
        middleName,
        lastName,
        emailAddress,
        employer,
        jobTitle,
        isCompleted,
      }),
    );
  };

  return application.creatingApplication || !refData || !plantDesignLicenceTypes ? (
    <Row>
      <Col span={12}>
        <ComponentLoader fullPage />
      </Col>
    </Row>
  ) : (
    <>
      {showErrors && !!errorsSummary?.length && <ErrorSummary errors={errorsSummary} />}

      <Form>
        <Row>
          <Col span={4}>
            <FormGroup
              id="firstName"
              label={PlantDesignApplicantDetailsPage.firstNameLabel}
              errorMessage={errors?.firstName?.errorMessage}
              hasError={showErrors && errors?.firstName?.invalid}
              margin={{ top: 'none' }}
            >
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} inputWidth="xl" />
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col span={4}>
            <FormGroup
              id="middleName"
              label={PlantDesignApplicantDetailsPage.middleNameLabel}
              errorMessage={errors?.middleName?.errorMessage}
              hasError={showErrors && errors?.middleName?.invalid}
              margin={{ top: 'lg' }}
            >
              <Input value={middleName} onChange={(e) => setMiddleName(e.target.value)} inputWidth="xl" />
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col span={4}>
            <FormGroup
              id="lastName"
              label={PlantDesignApplicantDetailsPage.lastNameLabel}
              errorMessage={errors?.lastName?.errorMessage}
              hasError={showErrors && errors?.lastName?.invalid}
              margin={{ top: 'lg' }}
            >
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} inputWidth="xl" />
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col span={6}>
            <FormGroup
              id="employer"
              label={PlantDesignApplicantDetailsPage.employerLabel}
              helpMessage={PlantDesignApplicantDetailsPage.employerHelpMessage}
              errorMessage={errors?.employer?.errorMessage}
              hasError={showErrors && errors?.employer?.invalid}
              margin={{ top: 'lg' }}
            >
              <Input value={employer} onChange={(e) => setEmployer(e.target.value)} inputWidth="xl" />
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col span={6}>
            <FormGroup
              id="jobTitle"
              label={PlantDesignApplicantDetailsPage.jobTitleLabel}
              errorMessage={errors?.jobTitle?.errorMessage}
              hasError={showErrors && errors?.jobTitle?.invalid}
              margin={{ top: 'lg' }}
            >
              <Input value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} inputWidth="xl" />
            </FormGroup>
          </Col>
        </Row>
      </Form>

      <PasswordlessContinueSaveExitButtons
        onContinueClick={onContinueClick}
        onPreSaving={onPreSaving}
        licenceType={PlantDesignConstants.licenceType}
      />
    </>
  );
};
